import { useEffect } from "react";
import { buildMeta } from "../lib/seo";
import { SITE_NAME } from "../lib/site";

const upsertMeta = (selector, attributes) => {
  let node = document.head.querySelector(selector);

  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    node.setAttribute(key, value);
  });
};

const upsertLink = (rel, href) => {
  let node = document.head.querySelector(`link[rel="${rel}"]`);

  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", rel);
    document.head.appendChild(node);
  }

  node.setAttribute("href", href);
};

const removeJsonLdGroup = (group) => {
  document.head
    .querySelectorAll(`script[data-seo-jsonld="${group}"]`)
    .forEach((node) => node.remove());
};

const SEOHead = ({
  title,
  description,
  path = "/",
  type = "website",
  image,
  keywords,
  noIndex = false,
  schema = [],
}) => {
  const imageAlt = title
    ? `${title.replace(` | ${SITE_NAME}`, "")} - ${SITE_NAME}`
    : "Preview produk Teras Alami";

  useEffect(() => {
    const meta = buildMeta({
      title,
      description,
      path,
      type,
      image,
      keywords,
      noIndex,
    });

    document.documentElement.lang = "id";
    document.title = meta.title;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: meta.description,
    });
    upsertMeta('meta[name="keywords"]', {
      name: "keywords",
      content: meta.keywords.join(", "),
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: meta.robots,
    });
    upsertMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content: meta.robots,
    });
    upsertMeta('meta[name="author"]', {
      name: "author",
      content: SITE_NAME,
    });
    upsertMeta('meta[name="publisher"]', {
      name: "publisher",
      content: SITE_NAME,
    });
    upsertMeta('meta[name="subject"]', {
      name: "subject",
      content: "Produk herbal alami dan gaya hidup sehat keluarga",
    });
    upsertMeta('meta[name="category"]', {
      name: "category",
      content: "Herbal, Wellness, Healthy Lifestyle",
    });

    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: "id_ID",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: meta.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: meta.description,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: meta.type,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: meta.canonical,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: meta.image,
    });
    upsertMeta('meta[property="og:image:url"]', {
      property: "og:image:url",
      content: meta.image,
    });
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: meta.image,
    });
    upsertMeta('meta[property="og:image:type"]', {
      property: "og:image:type",
      content: "image/jpeg",
    });
    upsertMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsertMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: imageAlt,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: meta.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: meta.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: meta.image,
    });
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: imageAlt,
    });

    upsertLink("canonical", meta.canonical);

    removeJsonLdGroup("dynamic");
    schema.filter(Boolean).forEach((item) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "dynamic";
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });

    return () => {
      removeJsonLdGroup("dynamic");
    };
  }, [description, image, keywords, noIndex, path, schema, title, type]);

  return null;
};

export default SEOHead;
